import type {
  MinorUnits,
  PayPeriod,
  PayRun,
  PayRunLineItem,
  StatutorySummary,
} from '../../data/types';

/** The seven steps of the run-payroll wizard, in order. */
export const WIZARD_STEPS = [
  'period_scope',
  'review_pay',
  'statutory_summary',
  'exceptions',
  'approval',
  'final_review',
  'post_run',
] as const;

export type WizardStep = (typeof WIZARD_STEPS)[number];

export const FIRST_STEP_NUMBER = 1;
export const LAST_STEP_NUMBER = WIZARD_STEPS.length;

/** Clamp a step number into range — a bad URL must not break the wizard. */
export function clamp_step(step: number): number {
  if (!Number.isFinite(step)) return FIRST_STEP_NUMBER;
  return Math.min(
    LAST_STEP_NUMBER,
    Math.max(FIRST_STEP_NUMBER, Math.trunc(step)),
  );
}

export function step_at(step_number: number): WizardStep {
  // clamp_step guarantees an in-range index, so the lookup always resolves.
  return WIZARD_STEPS[clamp_step(step_number) - 1] ?? WIZARD_STEPS[0];
}

export type StepState = 'done' | 'current' | 'upcoming';

export function step_state(
  step_number: number,
  current_step: number,
): StepState {
  if (step_number < current_step) return 'done';
  if (step_number === current_step) return 'current';
  return 'upcoming';
}

/**
 * Net pay for one line: gross, less every deduction withheld from it.
 *
 * `allowances` is deliberately NOT added here (#24395) — it is a memo
 * breakdown of `gross`, which the payroll engine already computed inclusive
 * of every assigned earnings pay item (see `PayRunLineItem.allowances`).
 * Adding it would pay every allowance twice.
 *
 * Integer arithmetic on minor units only — never float.
 */
export function line_net(line: PayRunLineItem): MinorUnits {
  return line.gross - line.deductions;
}

export interface LineItemTotals {
  gross: MinorUnits;
  allowances: MinorUnits;
  deductions: MinorUnits;
  net: MinorUnits;
}

/** Column totals for the lines currently visible in the review step. */
export function total_line_items(
  lines: readonly PayRunLineItem[],
): LineItemTotals {
  return lines.reduce<LineItemTotals>(
    (totals, line) => ({
      gross: totals.gross + line.gross,
      allowances: totals.allowances + line.allowances,
      deductions: totals.deductions + line.deductions,
      net: totals.net + line_net(line),
    }),
    { gross: 0, allowances: 0, deductions: 0, net: 0 },
  );
}

/**
 * Run-level net payable, derived from its own totals:
 * `gross - statutory - other`.
 *
 * `total_allowances` is a memo breakdown of `gross_wages` (#24395), so it
 * never appears in this identity — the summary card shows it for
 * information, exactly as a payslip shows the earnings that make up gross.
 *
 * This exists ONLY to detect a mismatch against the API's authoritative
 * `net_payable` (see `net_payable_reconciles`). It is never rendered as the
 * card's Net payable: the API figure is what the employees actually get
 * paid, while this one can contradict it — or go negative — whenever
 * `deriveDeductionSplit` had to clamp a contradictory residual at 0
 * (`wagepro_store_pay_runs.ts`). A failing reconciliation is disclosed in
 * the summary card's note, not fixed up by printing a computed total.
 */
export function derived_net_payable(run: PayRun): MinorUnits {
  return run.gross_wages - run.statutory_deductions - run.other_deductions;
}

export function net_payable_reconciles(run: PayRun): boolean {
  return derived_net_payable(run) === run.net_payable;
}

/** Total employer-side statutory cost on top of gross wages. */
export function employer_contributions(
  statutory: StatutorySummary,
): MinorUnits {
  return statutory.epf_employer + statutory.etf_employer;
}

export function unacknowledged_exceptions(run: PayRun): number {
  return run.exceptions.filter((exception) => !exception.acknowledged).length;
}

/**
 * The exceptions step is a gate: the run cannot move past it while an
 * exception is outstanding. Every other step advances freely.
 */
export function can_advance(run: PayRun, current_step: number): boolean {
  if (current_step >= LAST_STEP_NUMBER) return false;
  if (step_at(current_step) === 'exceptions') {
    return unacknowledged_exceptions(run) === 0;
  }
  return true;
}

export function can_go_back(current_step: number): boolean {
  return current_step > FIRST_STEP_NUMBER;
}

export function next_step(run: PayRun, current_step: number): number {
  return can_advance(run, current_step)
    ? clamp_step(current_step + 1)
    : clamp_step(current_step);
}

export function previous_step(current_step: number): number {
  return clamp_step(current_step - 1);
}

/** Sign-off is only offered once the preparer has submitted the run. */
export function can_sign_off(run: PayRun): boolean {
  return (
    run.approval.preparer_state === 'submitted' &&
    run.approval.approver_state !== 'approved'
  );
}

/**
 * Default period for the "Start pay run" affordance on the empty state
 * (C4b, #21955) — the current calendar month. Pure and UTC-based so it's
 * stable regardless of the caller's local timezone.
 */
export function current_month_period(now: Date = new Date()): {
  period_start: string;
  period_end: string;
} {
  return period_bounds({
    year: now.getUTCFullYear(),
    month: now.getUTCMonth() + 1,
  });
}

/** First and last calendar day (`YYYY-MM-DD`) of a monthly pay period. */
export function period_bounds(period: PayPeriod): {
  period_start: string;
  period_end: string;
} {
  const pad2 = (value: number): string => String(value).padStart(2, '0');
  const lastDay = new Date(Date.UTC(period.year, period.month, 0)).getUTCDate();
  const prefix = `${String(period.year)}-${pad2(period.month)}`;
  return {
    period_start: `${prefix}-01`,
    period_end: `${prefix}-${pad2(lastDay)}`,
  };
}

/** Stable `YYYY-MM` key for a pay period (select option value). */
export function period_key(period: PayPeriod): string {
  return `${String(period.year)}-${String(period.month).padStart(2, '0')}`;
}

/** How many months back (including the current one) the picker offers. */
export const SELECTABLE_PAST_MONTHS = 12;

export interface SelectablePeriod {
  period: PayPeriod;
  /** A non-voided pay run already covers this month — the API would 409. */
  already_run: boolean;
}

/**
 * Months the employer may choose to process, newest first: next month (to
 * allow preparing a run ahead of time), the current month, and the
 * preceding {@link SELECTABLE_PAST_MONTHS} - 1 months for late runs. Months
 * already covered by a non-voided pay run are flagged so the picker can
 * disable them instead of letting `createPayRun` reject with CONFLICT.
 */
export function selectable_periods(
  existing_runs: readonly Pick<PayRun, 'period' | 'status'>[],
  now: Date = new Date(),
): SelectablePeriod[] {
  const taken = new Set(
    existing_runs
      .filter((run) => run.status !== 'voided')
      .map((run) => period_key(run.period)),
  );
  const out: SelectablePeriod[] = [];
  for (let offset = 1; offset > -SELECTABLE_PAST_MONTHS; offset -= 1) {
    const d = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1),
    );
    const period = { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
    out.push({ period, already_run: taken.has(period_key(period)) });
  }
  return out;
}

/**
 * The period the picker preselects: the current month if it is still open,
 * else the next open month after it, else the most recent open month before
 * it. `undefined` only when every offered month already has a pay run.
 */
export function default_selected_period(
  options: readonly SelectablePeriod[],
  now: Date = new Date(),
): PayPeriod | undefined {
  const current = period_key({
    year: now.getUTCFullYear(),
    month: now.getUTCMonth() + 1,
  });
  const open = options.filter((o) => !o.already_run);
  const current_open = open.find((o) => period_key(o.period) === current);
  if (current_open) return current_open.period;
  const future_open = open.find((o) => period_key(o.period) > current);
  if (future_open) return future_open.period;
  return open[0]?.period;
}

export type PayRunViewMode = 'wizard' | 'complete';

/**
 * Statuses past approval. The real backend never moves a run beyond
 * `approved` (`finalisePayRun` does not change status), so an approved run
 * is as finished as a paid/filed one: the wizard has nothing left to do.
 */
const COMPLETE_STATUSES: ReadonlySet<PayRun['status']> = new Set([
  'approved',
  'paid',
  'filed',
]);

/**
 * What Run payroll shows for the current run: the seven-step wizard while
 * it is still being prepared (`draft`/`in_review`), or the completion view
 * once it is approved (or later). `voided` stays `'wizard'` -- the screen
 * never reaches this for a voided run (it shows the start panel instead).
 */
export function pay_run_view_mode(run: Pick<PayRun, 'status'>): PayRunViewMode {
  return COMPLETE_STATUSES.has(run.status) ? 'complete' : 'wizard';
}

/**
 * Identity of "which run, in which state" the screen is showing. When this
 * changes (a new run, or the same run reloaded as approved after sign-off)
 * the screen resets its wizard step and closes the details view, so a
 * successful sign-off lands on the completion view instead of a frozen
 * Approval step.
 */
export function pay_run_view_key(run: Pick<PayRun, 'id' | 'status'>): string {
  return `${run.id}:${run.status}`;
}

/**
 * The month "Start the next pay run" preselects: exactly the period the
 * start panel's picker defaults to, so the CTA label and the panel agree.
 * `undefined` when every offered month already has a pay run.
 */
export function next_pay_run_period(
  existing_runs: readonly Pick<PayRun, 'period' | 'status'>[],
  now: Date = new Date(),
): PayPeriod | undefined {
  return default_selected_period(selectable_periods(existing_runs, now), now);
}
